import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Paperclip, Trash2, Plus, Eye, Loader2, AlertTriangle, Upload, Users, X, Bold, Italic, Underline, Save } from "lucide-react";
import { api, fmtErr } from "../../lib/api";

const MERGE_FIELDS = ["{first_name}", "{last_name}", "{company}", "{property_address}", "{area}", "{agent_name}"];
const MAX_BYTES = 5 * 1024 * 1024;

function mergePreview(text, r, agentName) {
  return (text || "")
    .replaceAll("{first_name}", r?.first_name || "there")
    .replaceAll("{last_name}", r?.last_name || "")
    .replaceAll("{company}", r?.company || "")
    .replaceAll("{property_address}", r?.property_address || "your property")
    .replaceAll("{area}", r?.area || "Vancouver")
    .replaceAll("{agent_name}", agentName || "Tendly");
}

export default function EmailCentre() {
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState({ companies: [], leads: [] });
  const [library, setLibrary] = useState([]);
  const [log, setLog] = useState([]);

  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [attachIds, setAttachIds] = useState([]);
  const [search, setSearch] = useState("");
  const [paste, setPaste] = useState("");
  const [csvCols, setCsvCols] = useState(null);
  const [csvMap, setCsvMap] = useState({ email: "", first_name: "", company: "" });
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [batch, setBatch] = useState(null);
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);

  const load = () => {
    api.get("/admin/email/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/admin/email/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/admin/email/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/email/attachments").then((r) => setLibrary(r.data)).catch(() => {});
    api.get("/admin/email/log").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const pickTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setSubject(t.subject);
      if (bodyRef.current) bodyRef.current.innerText = t.body;
    }
  };

  const addRecipients = (list) => {
    setRecipients((cur) => {
      const seen = new Set(cur.map((r) => r.email));
      return [...cur, ...list.filter((r) => r.email && !seen.has(r.email))];
    });
  };

  const addPasted = () => {
    const emails = paste.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e.includes("@"));
    if (!emails.length) return toast.error("No valid emails found");
    addRecipients(emails.map((e) => ({ email: e, first_name: "", source: "pasted list" })));
    setPaste("");
  };

  const parseCsv = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const lines = String(reader.result).split("\n").filter((l) => l.trim());
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/[\s-]+/g, "_"));
      const rows = lines.slice(1).map((l) => l.split(",").map((c) => c.trim()));
      setCsvCols({ headers, rows });
      setCsvMap({
        email: headers.find((h) => h.includes("email")) || "",
        first_name: headers.find((h) => h.includes("first") || h === "name") || "",
        company: headers.find((h) => h.includes("company")) || "",
      });
    };
    reader.readAsText(file);
  };

  const addCsv = () => {
    if (!csvMap.email) return toast.error("Map the email column first");
    const ei = csvCols.headers.indexOf(csvMap.email);
    const fi = csvCols.headers.indexOf(csvMap.first_name);
    const ci = csvCols.headers.indexOf(csvMap.company);
    const list = csvCols.rows.map((row) => ({
      email: (row[ei] || "").toLowerCase(), first_name: fi >= 0 ? row[fi] : "",
      company: ci >= 0 ? row[ci] : "", source: "csv upload",
    })).filter((r) => r.email.includes("@"));
    addRecipients(list);
    setCsvCols(null);
    toast.success(`${list.length} recipients added from CSV`);
  };

  const uploadFile = (file) => {
    const allowed = ["application/pdf", "image/png", "image/jpeg", "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"];
    if (!allowed.includes(file.type)) return toast.error("Only PDF, PPTX, PNG and JPG are allowed");
    if (file.size > MAX_BYTES) return toast.error("File exceeds the 5 MB limit");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/admin/email/attachments", {
          name: file.name, content_type: file.type,
          content_base64: String(reader.result).split(",")[1],
        });
        toast.success("Attachment added to library");
        load();
      } catch (e) { toast.error(fmtErr(e)); }
    };
    reader.readAsDataURL(file);
  };

  const attachedBytes = useMemo(() => library.filter((a) => attachIds.includes(a.id)).reduce((s, a) => s + a.size, 0), [library, attachIds]);

  const filteredCompanies = contacts.companies.filter((c) => !search || `${c.company} ${c.email}`.toLowerCase().includes(search.toLowerCase()));
  const filteredLeads = contacts.leads.filter((c) => !search || `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(search.toLowerCase()));

  const openConfirm = () => {
    if (!subject.trim()) return toast.error("Add a subject");
    if (!bodyRef.current?.innerText.trim()) return toast.error("Add a body");
    if (!recipients.length) return toast.error("Add at least one recipient");
    if (attachedBytes > MAX_BYTES) return toast.error("Attachments exceed the 5 MB total limit — remove files before sending");
    setConfirmed(false);
    setConfirming(true);
  };

  const doSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/email/send", {
        subject, body: bodyRef.current.innerText, recipients,
        attachment_ids: attachIds, template_name: templates.find((t) => t.id === templateId)?.name,
      });
      if (r.data.skipped_suppressed?.length) toast.warning(`${r.data.skipped_suppressed.length} suppressed address(es) skipped`);
      setConfirming(false);
      setBatch(r.data);
      pollBatch(r.data.batch_id);
    } catch (e) { toast.error(fmtErr(e)); setConfirming(false); }
    setSending(false);
  };

  const pollBatch = (bid) => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/admin/email/batch/${bid}`);
        setBatch((b) => ({ ...b, ...r.data }));
        if (r.data.status === "done") {
          clearInterval(t);
          toast.success(`Send complete: ${r.data.sent} sent, ${r.data.failed} failed, ${r.data.skipped} skipped`);
          load();
        }
      } catch { clearInterval(t); }
    }, 1500);
  };

  const mark = async (sid, status) => {
    try {
      const r = await api.post(`/admin/email/log/${sid}/mark`, { status });
      if (r.data.suppressed) toast.success("Added to suppression list — future sends blocked");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveTemplate = async (asNew) => {
    const body = bodyRef.current?.innerText || "";
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    try {
      if (asNew || !templateId) {
        const name = window.prompt("Template name:") || "Untitled";
        await api.post("/admin/email/templates", { name, subject, body });
        toast.success("Template saved");
      } else {
        await api.put(`/admin/email/templates/${templateId}`, { subject, body });
        toast.success("Template updated");
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const exec = (cmd) => { document.execCommand(cmd, false, null); bodyRef.current?.focus(); };
  const firstRecipient = recipients[0];
  const bulk = recipients.length > 1;

  return (
    <div data-testid="email-centre" className="max-w-5xl">
      <h1 className="font-heading font-bold text-2xl tracking-tight">Email Centre</h1>
      <p className="text-sm text-muted-foreground mb-6">Operator outreach to landlords, companies, and renters — CASL-compliant by default.</p>

      {config && !config.domain_configured && (
        <div data-testid="domain-warning" className="mb-6 rounded-2xl border border-coral-500/40 bg-coral-50 dark:bg-coral-800/20 p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-coral-600 shrink-0 mt-0.5" />
          <p className="text-sm text-coral-800 dark:text-coral-400">No verified sending domain configured (<span className="font-mono">EMAIL_FROM_DOMAIN</span>). Deliverability will be poor and bulk sending should not be used until one is set.</p>
        </div>
      )}
      {config && (
        <div className="mb-6 flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span>Sent this hour: <strong>{config.sent_last_hour}/{config.hourly_cap}</strong></span>
          <span>Today: <strong>{config.sent_today}/{config.daily_cap}</strong></span>
          <span>Bounce rate: <strong>{config.bounce_rate}%</strong></span>
          <span>Complaint rate: <strong>{config.complaint_rate}%</strong></span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compose */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading font-semibold text-lg">Compose</h2>
          <div className="mt-3 flex gap-2">
            <select data-testid="template-select" value={templateId} onChange={(e) => pickTemplate(e.target.value)} aria-label="Template"
              className="flex-1 rounded-xl border border-input bg-background px-3 py-2 text-sm">
              <option value="">— Choose a template —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="template-save" onClick={() => saveTemplate(false)} aria-label="Save template" className="p-2 rounded-xl border border-border hover:bg-secondary"><Save className="w-4 h-4" /></button>
            <button data-testid="template-save-new" onClick={() => saveTemplate(true)} aria-label="Save as new template" className="p-2 rounded-xl border border-border hover:bg-secondary"><Plus className="w-4 h-4" /></button>
            {templateId && (
              <button data-testid="template-delete" onClick={async () => { await api.delete(`/admin/email/templates/${templateId}`); setTemplateId(""); load(); }} aria-label="Delete template" className="p-2 rounded-xl border border-border text-coral-600 hover:bg-coral-50"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
          <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line"
            aria-label="Subject" className="mt-3 w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
          <div className="mt-3 flex items-center gap-1 border border-input rounded-t-xl bg-secondary/50 px-2 py-1.5">
            <button data-testid="fmt-bold" onClick={() => exec("bold")} aria-label="Bold" className="p-1.5 rounded hover:bg-secondary"><Bold className="w-3.5 h-3.5" /></button>
            <button data-testid="fmt-italic" onClick={() => exec("italic")} aria-label="Italic" className="p-1.5 rounded hover:bg-secondary"><Italic className="w-3.5 h-3.5" /></button>
            <button data-testid="fmt-underline" onClick={() => exec("underline")} aria-label="Underline" className="p-1.5 rounded hover:bg-secondary"><Underline className="w-3.5 h-3.5" /></button>
            <span className="mx-2 text-border">|</span>
            {MERGE_FIELDS.map((f) => (
              <button key={f} data-testid={`merge-${f.replace(/[{}]/g, "")}`} onClick={() => { if (bodyRef.current) { bodyRef.current.innerText += " " + f; } }}
                className="rounded-full bg-teal-50 dark:bg-teal-900/40 text-primary px-2 py-0.5 text-[10px] font-mono font-semibold">{f}</button>
            ))}
          </div>
          <div data-testid="email-body" ref={bodyRef} contentEditable role="textbox" aria-label="Email body" aria-multiline="true"
            className="min-h-[180px] rounded-b-xl border border-t-0 border-input bg-background px-4 py-3 text-sm leading-relaxed focus:outline-none whitespace-pre-wrap" />
          <p className="mt-2 text-xs text-muted-foreground">CASL footer (Tendly identification + one-click unsubscribe) is appended automatically to every send.</p>
          <p data-testid="reply-to-note" className="mt-1 text-xs text-muted-foreground">Replies to every send land in <strong>tendly.concierge@gmail.com</strong> — check that inbox for responses. The From address stays on Tendly's verified sending domain.</p>

          {/* Attachments */}
          <div className="mt-4 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold flex items-center gap-1.5"><Paperclip className="w-4 h-4" /> Attachments</p>
              <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary">
                <Upload className="w-3.5 h-3.5" /> Upload
                <input data-testid="attachment-upload" type="file" accept=".pdf,.png,.jpg,.jpeg,.ppt,.pptx" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} />
              </label>
            </div>
            <p className={`mt-1 text-xs ${attachedBytes > MAX_BYTES ? "text-coral-600 font-semibold" : "text-muted-foreground"}`}>
              Attached: {(attachedBytes / 1024 / 1024).toFixed(1)} MB / 5 MB{attachedBytes > MAX_BYTES ? " — over limit, send will be blocked!" : ""}
            </p>
            <ul className="mt-2 space-y-1.5">
              {library.map((a) => (
                <li key={a.id} className="flex items-center gap-2 text-sm">
                  <input data-testid={`attach-${a.id}`} type="checkbox" checked={attachIds.includes(a.id)}
                    onChange={(e) => setAttachIds(e.target.checked ? [...attachIds, a.id] : attachIds.filter((x) => x !== a.id))}
                    className="accent-[#0D5C63]" aria-label={`Attach ${a.name}`} />
                  <span className="flex-1 truncate">{a.name}</span>
                  <span className="text-xs text-muted-foreground">{(a.size / 1024).toFixed(0)} KB</span>
                  <button data-testid={`attach-delete-${a.id}`} onClick={async () => { await api.delete(`/admin/email/attachments/${a.id}`); load(); }} aria-label="Delete attachment" className="text-coral-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
              {!library.length && <li className="text-xs text-muted-foreground">No attachments yet — upload the pitch deck once and reuse it.</li>}
            </ul>
          </div>
        </section>

        {/* Recipients */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Recipients <span data-testid="recipient-count" className="rounded-full bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5">{recipients.length}</span></h2>
          <input data-testid="contact-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search CRM contacts…" aria-label="Search contacts"
            className="mt-3 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm" />
          <div className="mt-3 max-h-44 overflow-y-auto space-y-1">
            {filteredCompanies.map((c) => (
              <label key={c.email} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-secondary cursor-pointer">
                <input data-testid={`crm-company-${c.email}`} type="checkbox" checked={recipients.some((r) => r.email === c.email)}
                  onChange={(e) => e.target.checked ? addRecipients([c]) : setRecipients(recipients.filter((r) => r.email !== c.email))}
                  className="accent-[#0D5C63]" />
                <span className="flex-1 truncate">{c.company} <span className="text-muted-foreground">· {c.email}</span></span>
                <span className="text-[10px] text-muted-foreground">company</span>
              </label>
            ))}
            {filteredLeads.map((c) => (
              <label key={c.email} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-secondary cursor-pointer">
                <input data-testid={`crm-lead-${c.email}`} type="checkbox" checked={recipients.some((r) => r.email === c.email)}
                  onChange={(e) => e.target.checked ? addRecipients([c]) : setRecipients(recipients.filter((r) => r.email !== c.email))}
                  className="accent-[#0D5C63]" />
                <span className="flex-1 truncate">{c.first_name} {c.last_name} <span className="text-muted-foreground">· {c.email}</span></span>
                <span className="text-[10px] text-muted-foreground">renter</span>
              </label>
            ))}
          </div>
          <div className="mt-3">
            <textarea data-testid="paste-recipients" value={paste} onChange={(e) => setPaste(e.target.value)} rows={2}
              placeholder="Paste emails, comma or line separated…" aria-label="Paste recipients"
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-xs font-mono" />
            <div className="mt-2 flex gap-2">
              <button data-testid="paste-add" onClick={addPasted} className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold">Add pasted</button>
              <label className="cursor-pointer rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:bg-secondary">
                Upload CSV
                <input data-testid="csv-upload" type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && parseCsv(e.target.files[0])} />
              </label>
            </div>
            {csvCols && (
              <div className="mt-3 rounded-xl bg-secondary p-3">
                <p className="text-xs font-semibold mb-2">Map CSV columns ({csvCols.rows.length} rows)</p>
                <div className="flex flex-wrap gap-2">
                  {[["email", "Email *"], ["first_name", "First name"], ["company", "Company"]].map(([k, label]) => (
                    <label key={k} className="text-xs">{label}
                      <select data-testid={`csv-map-${k}`} value={csvMap[k]} onChange={(e) => setCsvMap({ ...csvMap, [k]: e.target.value })}
                        className="ml-1 rounded-lg border border-input bg-background px-2 py-1">
                        <option value="">—</option>
                        {csvCols.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </label>
                  ))}
                  <button data-testid="csv-add" onClick={addCsv} className="rounded-full bg-primary text-primary-foreground px-4 py-1 text-xs font-semibold">Add rows</button>
                </div>
              </div>
            )}
          </div>
          {recipients.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {recipients.map((r) => (
                <span key={r.email} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs">
                  {r.email}
                  <button onClick={() => setRecipients(recipients.filter((x) => x.email !== r.email))} aria-label={`Remove ${r.email}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          {/* Preview */}
          {firstRecipient && (
            <div className="mt-4 rounded-xl border border-border bg-secondary/40 p-4">
              <p className="text-xs font-semibold flex items-center gap-1.5 mb-2"><Eye className="w-3.5 h-3.5" /> Merged preview (for {firstRecipient.email})</p>
              <p data-testid="preview-subject" className="text-sm font-semibold">{mergePreview(subject, firstRecipient, "Your name")}</p>
              <p data-testid="preview-body" className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{mergePreview(bodyRef.current?.innerText || "", firstRecipient, "Your name").slice(0, 400)}</p>
            </div>
          )}

          <button data-testid="email-send-btn" onClick={openConfirm}
            className="mt-5 w-full rounded-full bg-primary text-primary-foreground py-3 font-semibold inline-flex items-center justify-center gap-2">
            <Send className="w-4 h-4" /> Send to {recipients.length} recipient{recipients.length !== 1 ? "s" : ""}
          </button>
        </section>
      </div>

      {/* Batch progress */}
      {batch && (
        <div data-testid="batch-progress" className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">{batch.status === "done" ? "Send complete" : "Sending…"}</span>
            <span className="text-muted-foreground">{(batch.sent || 0) + (batch.failed || 0)} / {batch.total || batch.queued} · {batch.failed || 0} failed · {batch.skipped || 0} suppressed skipped</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${((((batch.sent || 0) + (batch.failed || 0)) / (batch.total || batch.queued || 1)) * 100)}%` }} />
          </div>
        </div>
      )}

      {/* Sent log */}
      <section className="mt-6 rounded-2xl border border-border bg-card overflow-x-auto">
        <h2 className="font-heading font-semibold text-lg px-5 pt-5">Sent log</h2>
        <table className="w-full text-sm min-w-[720px] mt-3">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-5 py-2">Recipient</th><th className="px-5 py-2">Subject</th><th className="px-5 py-2">Template</th><th className="px-5 py-2">Date</th><th className="px-5 py-2">Status</th><th className="px-5 py-2">Mark</th>
          </tr></thead>
          <tbody>
            {log.map((e) => (
              <tr key={e.id} data-testid={`log-${e.id}`} className="border-b border-border/60 last:border-0">
                <td className="px-5 py-2.5">{e.recipient}<p className="text-[10px] text-muted-foreground">{e.source}</p></td>
                <td className="px-5 py-2.5 text-xs max-w-[200px] truncate" title={e.subject}>{e.subject}
                  {e.attachments?.length > 0 && (
                    <p data-testid={`log-attachments-${e.id}`} className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Paperclip className="w-3 h-3" /> {e.attachments.join(", ")}
                    </p>
                  )}
                </td>
                <td className="px-5 py-2.5 text-xs">{e.template_name || "—"}</td>
                <td className="px-5 py-2.5 text-xs text-muted-foreground">{e.sent_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-5 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${e.status === "sent" ? "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100" : e.status === "failed" || e.status === "bounced" ? "bg-coral-100 text-coral-800" : "bg-secondary"}`}>{e.status}</span></td>
                <td className="px-5 py-2.5">
                  <select data-testid={`log-mark-${e.id}`} value="" onChange={(ev) => ev.target.value && mark(e.id, ev.target.value)} aria-label="Mark status"
                    className="rounded-lg border border-input bg-background px-2 py-1 text-xs">
                    <option value="">Mark…</option>
                    <option value="opened">Opened</option><option value="replied">Replied</option>
                    <option value="bounced">Bounced</option><option value="complained">Spam complaint</option>
                    <option value="unsubscribed">Unsubscribed</option>
                  </select>
                </td>
              </tr>
            ))}
            {!log.length && <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground">No emails sent yet.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Bulk confirm modal */}
      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Confirm send">
          <div data-testid="send-confirm" className="w-full max-w-md rounded-2xl border border-border bg-card p-6">
            <h2 className="font-heading font-semibold text-xl">Confirm send</h2>
            <p className="mt-2 text-sm">You are about to send <strong>{recipients.length} individual email{recipients.length !== 1 ? "s" : ""}</strong>. Recipients never see each other's addresses.</p>
            {bulk && (
              <p className="mt-3 rounded-xl bg-coral-50 dark:bg-coral-800/20 border border-coral-100 dark:border-coral-800 p-3 text-xs text-coral-800 dark:text-coral-400 leading-relaxed">
                CASL reminder: only send to business email addresses that are publicly published and where the message relates to the recipient's role. Every email includes Tendly's identification and a one-click unsubscribe.
              </p>
            )}
            <label className="mt-4 flex items-start gap-2.5 text-sm cursor-pointer">
              <input data-testid="send-confirm-checkbox" type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 accent-[#0D5C63]" />
              <span>I confirm these recipients are appropriate to contact under CASL.</span>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="send-cancel" onClick={() => setConfirming(false)} className="rounded-full border border-border px-5 py-2 text-sm font-medium">Cancel</button>
              <button data-testid="send-confirm-btn" onClick={doSend} disabled={!confirmed || sending}
                className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-2">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
